import { useAtomValue } from "@effect/atom-react";
import { Atom } from "effect/unstable/reactivity";

import {
  createCloudAuthRecovery,
  createConnectionSupervisor,
  getWsReconnectDelayMsForRetry as sharedGetWsReconnectDelayMsForRetry,
  UPSTREAM_BACKOFF_RESET_AFTER_MS,
  WS_RECONNECT_MAX_RETRIES as WS_RECONNECT_MAX_RETRIES_SHARED,
} from "@cmmd-forge/client-runtime";

import { appAtomRegistry } from "./atomRegistry";

export type WsConnectionUiState = "connected" | "connecting" | "error" | "offline" | "reconnecting";
export type WsReconnectPhase = "attempting" | "exhausted" | "idle" | "waiting";

// 10 retries = 1+2+4+8+16+32+64+64+64+64 ≈ 319s wall-time. Covers the
// expected Sprite start/connect budget with margin. Backstop for FINDING-CB1
// cold-boot timeout race;
// the long-term phase channel (docs/superpowers/specs/2026-05-19-forge-cold-
// boot-progress-channel-design.md) drives the real fix.
export const WS_RECONNECT_MAX_RETRIES = WS_RECONNECT_MAX_RETRIES_SHARED;
export const WS_RECONNECT_MAX_ATTEMPTS = WS_RECONNECT_MAX_RETRIES + 1;

export interface WsConnectionStatus {
  readonly attemptCount: number;
  readonly closeCode: number | null;
  readonly closeReason: string | null;
  readonly connectedAt: string | null;
  readonly disconnectedAt: string | null;
  readonly hasConnected: boolean;
  readonly lastError: string | null;
  readonly lastErrorAt: string | null;
  readonly nextRetryAt: string | null;
  readonly online: boolean;
  readonly phase: "idle" | "connecting" | "connected" | "disconnected";
  readonly reconnectAttemptCount: number;
  readonly reconnectMaxAttempts: number;
  readonly reconnectPhase: WsReconnectPhase;
  readonly socketUrl: string | null;
}

const INITIAL_WS_CONNECTION_STATUS = Object.freeze<WsConnectionStatus>({
  attemptCount: 0,
  closeCode: null,
  closeReason: null,
  connectedAt: null,
  disconnectedAt: null,
  hasConnected: false,
  lastError: null,
  lastErrorAt: null,
  nextRetryAt: null,
  online: typeof navigator === "undefined" ? true : navigator.onLine !== false,
  phase: "idle",
  reconnectAttemptCount: 0,
  reconnectMaxAttempts: WS_RECONNECT_MAX_ATTEMPTS,
  reconnectPhase: "idle",
  socketUrl: null,
});

const wsConnectionStatusAtom = Atom.make(INITIAL_WS_CONNECTION_STATUS).pipe(
  Atom.keepAlive,
  Atom.withLabel("ws-connection-status"),
);

// ── Connection supervisor (the decision owner) ───────────────────────────────
// The supervisor (ported from upstream's EnvironmentSupervisor decision core)
// owns every reconnect decision: phase transitions, the backoff ladder, when
// the ladder resets, and when retries are exhausted. The record* functions
// below are thin adapters that report transport events to it and mirror its
// decisions into the atom. The ladder stays forge's bounded one
// (1s doubling to 64s, WS_RECONNECT_MAX_RETRIES retries). Upstream's default
// ladder retries forever, which the web transport must not.
//
// Known split candidate: this file has grown to ~330 lines mixing the WS
// status atom, the supervisor adapters, and the cloud-auth-recovery adapters.
// The next phase's coordinator migration absorbs that split; do not extend it
// with a fourth concern in the meantime.

/** Forge's bounded WS ladder, derived from the shared delay helper. */
const WS_RETRY_LADDER_MS: ReadonlyArray<number> = Array.from(
  { length: WS_RECONNECT_MAX_RETRIES },
  (_, retryIndex) => sharedGetWsReconnectDelayMsForRetry(retryIndex) ?? 0,
);

function createSupervisors() {
  return {
    connection: createConnectionSupervisor({
      retryDelaysMs: WS_RETRY_LADDER_MS,
      maxRetries: WS_RECONNECT_MAX_RETRIES,
      backoffResetAfterMs: UPSTREAM_BACKOFF_RESET_AFTER_MS,
    }),
    cloudAuth: createCloudAuthRecovery(),
  };
}

let supervisors = createSupervisors();

// ── Cloud auth failure state ─────────────────────────────────────────────────
export type CloudAuthFailureSource = "http" | "ws-token" | "cmmd-runtimes";

export type CloudAuthFailureState =
  | { readonly status: "ok" }
  | {
      readonly status: "expired";
      readonly detectedAt: string;
      readonly source: CloudAuthFailureSource;
    };

const INITIAL_CLOUD_AUTH_FAILURE_STATE: CloudAuthFailureState = Object.freeze({ status: "ok" });

export const cloudAuthFailureAtom = Atom.make<CloudAuthFailureState>(
  INITIAL_CLOUD_AUTH_FAILURE_STATE,
).pipe(Atom.keepAlive, Atom.withLabel("cloud-auth-failure"));

/**
 * Transition to "expired" state. The idempotency lives in the supervisor's
 * auth-recovery latch: once expired, later reports keep the first detection
 * time and source, so concurrent HTTP and WS-token 401s don't thrash the
 * toast.
 */
export function setCloudAuthFailure(source: CloudAuthFailureSource): void {
  const recoveryState = supervisors.cloudAuth.reportAuthFailure(source);
  appAtomRegistry.set(cloudAuthFailureAtom, cloudAuthFailureFromRecovery(recoveryState));
}

export function clearCloudAuthFailure(): void {
  supervisors.cloudAuth.clearAuthFailure();
  appAtomRegistry.set(cloudAuthFailureAtom, INITIAL_CLOUD_AUTH_FAILURE_STATE);
}

function cloudAuthFailureFromRecovery(recoveryState: {
  readonly status: "ok" | "expired";
  readonly detectedAtMs: number | null;
  readonly source: CloudAuthFailureSource | null;
}): CloudAuthFailureState {
  if (recoveryState.status !== "expired" || recoveryState.detectedAtMs === null) {
    return INITIAL_CLOUD_AUTH_FAILURE_STATE;
  }
  return {
    status: "expired",
    detectedAt: new Date(recoveryState.detectedAtMs).toISOString(),
    source: recoveryState.source ?? "http",
  };
}

export function getCloudAuthFailureState(): CloudAuthFailureState {
  return appAtomRegistry.get(cloudAuthFailureAtom);
}

/**
 * State-level admission for a login-redirect flight. Defense in depth behind
 * the promise single-flight in cmmdAuthRedirect, which is the operative
 * guard: the two open and close inside the same window, so this layer adds a
 * second tripwire, not a separate guarantee.
 */
export function beginCloudReAuthRedirect(): boolean {
  return supervisors.cloudAuth.beginReAuth();
}

export function endCloudReAuthRedirect(): void {
  supervisors.cloudAuth.endReAuth();
}

export function resetCloudAuthFailureForTests(): void {
  supervisors = createSupervisors();
  appAtomRegistry.set(cloudAuthFailureAtom, INITIAL_CLOUD_AUTH_FAILURE_STATE);
}

function isoNow() {
  return new Date().toISOString();
}

function updateWsConnectionStatus(
  updater: (current: WsConnectionStatus) => WsConnectionStatus,
): WsConnectionStatus {
  const nextStatus = updater(getWsConnectionStatus());
  appAtomRegistry.set(wsConnectionStatusAtom, nextStatus);
  return nextStatus;
}

export function getWsConnectionStatus(): WsConnectionStatus {
  return appAtomRegistry.get(wsConnectionStatusAtom);
}

export function getWsConnectionUiState(status: WsConnectionStatus): WsConnectionUiState {
  if (status.phase === "connected") {
    return "connected";
  }

  if (status.hasConnected && status.disconnectedAt === null && status.phase === "connecting") {
    return "connected";
  }

  if (!status.online && (status.disconnectedAt !== null || status.phase === "disconnected")) {
    return "offline";
  }

  if (!status.hasConnected) {
    return status.phase === "disconnected" ? "error" : "connecting";
  }

  return "reconnecting";
}

/**
 * One supervisor failure per drop. A browser WebSocket fires error and then
 * close for the same failed connection, and without this window each report
 * would consume a ladder rung, so every real drop would burn two rungs and
 * retries would exhaust twice as early. A new window opens when an attempt
 * starts or a connection opens.
 */
let failureReportedForAttempt = false;

/**
 * Report that a connection attempt began. Reporting an attempt is also
 * wanting a connection: the supervisor's desire flips on here if needed.
 */
export function recordWsConnectionAttempt(socketUrl: string): WsConnectionStatus {
  if (!supervisors.connection.getState().desired) {
    supervisors.connection.connect();
  }
  failureReportedForAttempt = false;
  const supervisorState = supervisors.connection.attemptStarted();
  return updateWsConnectionStatus((current) => ({
    ...current,
    attemptCount: current.attemptCount + 1,
    nextRetryAt: null,
    phase: "connecting",
    reconnectAttemptCount: supervisorState.attempt,
    reconnectPhase: "attempting",
    socketUrl,
  }));
}

/** Report that the attempt established a connection and closes its failure window. */
export function recordWsConnectionOpened(): WsConnectionStatus {
  failureReportedForAttempt = false;
  supervisors.connection.attemptConnected();
  return updateWsConnectionStatus((current) => ({
    ...current,
    closeCode: null,
    closeReason: null,
    connectedAt: isoNow(),
    disconnectedAt: null,
    hasConnected: true,
    nextRetryAt: null,
    phase: "connected",
    reconnectAttemptCount: 0,
    reconnectPhase: "idle",
  }));
}

/**
 * Report a connection error. The supervisor hears at most one failure per
 * drop; a later close for the same drop only refreshes the atom's close
 * details.
 */
export function recordWsConnectionErrored(message?: string | null): WsConnectionStatus {
  if (!failureReportedForAttempt) {
    failureReportedForAttempt = true;
    supervisors.connection.attemptFailed({
      kind: "transient",
      message: message?.trim() ? message : "Unable to connect to the Forge server WebSocket.",
    });
  }
  return updateWsConnectionStatus((current) =>
    applyDisconnectState(current, {
      lastError: message?.trim() ? message : current.lastError,
      lastErrorAt: isoNow(),
    }),
  );
}

/**
 * Report a socket close. Clean lifecycle closes (1000/1001 with no reason)
 * are not drops at all. For a real drop the supervisor hears it only if the
 * error event has not already reported this drop.
 */
export function recordWsConnectionClosed(details?: {
  readonly code?: number;
  readonly reason?: string;
}): WsConnectionStatus {
  if (getWsConnectionStatus().hasConnected && isCleanWebSocketLifecycleClose(details)) {
    return getWsConnectionStatus();
  }
  if (!failureReportedForAttempt) {
    failureReportedForAttempt = true;
    supervisors.connection.attemptFailed({
      kind: "transient",
      message: details?.reason?.trim() || "WebSocket connection closed.",
    });
  }
  return updateWsConnectionStatus((current) =>
    applyDisconnectState(current, {
      closeCode: details?.code ?? current.closeCode,
      closeReason: details?.reason?.trim() ? details.reason : current.closeReason,
    }),
  );
}

function isCleanWebSocketLifecycleClose(details?: {
  readonly code?: number;
  readonly reason?: string;
}): boolean {
  const reason = details?.reason?.trim() ?? "";
  return reason.length === 0 && (details?.code === 1000 || details?.code === 1001);
}

/**
 * Report a browser online/offline change. The supervisor pauses retries while
 * offline and resumes the ladder when the network returns (upstream's
 * network-offline pause).
 */
export function setBrowserOnlineStatus(online: boolean): WsConnectionStatus {
  supervisors.connection.setNetwork(online ? "online" : "offline");
  return updateWsConnectionStatus((current) => ({
    ...current,
    online,
  }));
}

/**
 * Drop the current backoff wait and start the next attempt from rung one
 * (upstream's retryNow). Called by manual reconnects.
 */
export function resetWsReconnectBackoff(): WsConnectionStatus {
  supervisors.connection.retryNow();
  return updateWsConnectionStatus((current) => ({
    ...current,
    nextRetryAt: null,
    reconnectAttemptCount: 0,
    reconnectPhase: "idle",
  }));
}

export function resetWsConnectionStateForTests(): void {
  supervisors = createSupervisors();
  appAtomRegistry.set(wsConnectionStatusAtom, INITIAL_WS_CONNECTION_STATUS);
}

export function useWsConnectionStatus(): WsConnectionStatus {
  return useAtomValue(wsConnectionStatusAtom);
}

export function getWsReconnectDelayMsForRetry(retryIndex: number): number | null {
  return sharedGetWsReconnectDelayMsForRetry(retryIndex);
}

function applyDisconnectState(
  current: WsConnectionStatus,
  updates: Partial<
    Pick<WsConnectionStatus, "closeCode" | "closeReason" | "lastError" | "lastErrorAt">
  >,
): WsConnectionStatus {
  const supervisorState = supervisors.connection.getState();
  const disconnectedAt = current.disconnectedAt ?? isoNow();

  return {
    ...current,
    ...updates,
    disconnectedAt,
    nextRetryAt:
      supervisorState.retryAtMs === null ? null : new Date(supervisorState.retryAtMs).toISOString(),
    phase: "disconnected",
    reconnectPhase: supervisorState.retriesExhausted ? "exhausted" : "waiting",
  };
}
